/* Deal workspace dispatcher. Loads the deal (GET /deal/:id), renders the per-deal
   stepper and routes to the correct step screen (each fetches its own step data and
   renders its own <main>). Once a decision is logged, the stepper shows all seven
   ticked. */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import Stepper from '../components/Stepper';
import { Loading, ErrorState } from '../components/States';
import { useDealState } from '../state';
import { STEPS } from '../data/config';
import { fetchDeal } from '../api';

import Intake from './deal/Intake';
import Analysis from './deal/Analysis';
import Triage from './deal/Triage';
import Diligence from './deal/Diligence';
import Scenarios from './deal/Scenarios';
import Memo from './deal/Memo';
import Decision from './deal/Decision';

const SCREENS = { intake: Intake, analysis: Analysis, triage: Triage, diligence: Diligence, scenarios: Scenarios, memo: Memo, decision: Decision };

export default function Deal() {
	const { id, step } = useParams();
	const { isDecided } = useDealState();
	const [deal, setDeal] = useState(null);
	const [error, setError] = useState(false);

	const load = useCallback(async () => {
		setError(false); setDeal(null);
		const d = await fetchDeal(id);
		if (!d) { setError(true); return; }
		setDeal(d);
	}, [id]);
	useEffect(() => { load(); }, [load]);

	const stepId = STEPS.some(s => s.id === step) ? step : 'intake';

	if (error) return (<><Stepper dealId={id} activeStep={stepId} decided={false} /><main className="page screen"><ErrorState title="Deal not found" sub="This deal could not be loaded — it may have been removed." onRetry={load} /></main></>);
	if (!deal) return (<><Stepper dealId={id} activeStep={stepId} decided={false} /><main className="page screen"><Loading label="Loading deal…" /></main></>);

	const Screen = SCREENS[stepId] || Intake;
	// A recorded decision closes the deal — the backend rejects every mutation (read-only);
	// this flag drives the all-ticked stepper and the view-only banner. `deal.decided` is
	// authoritative (from GET /deal/:id); fall back to the session flag for immediacy.
	const locked = deal.decided || isDecided(deal.id);

	return (
		<>
			<Stepper dealId={deal.id} activeStep={stepId} decided={locked} />
			{locked && (
				<div className="page" style={{ paddingTop: 12, paddingBottom: 0 }}>
					<div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, borderColor: 'var(--dlg-blue)', background: 'var(--dlg-fill)' }}>
						<span style={{ fontSize: 18 }}>🔒</span>
						<div>
							<div style={{ fontWeight: 600, color: 'var(--dlg-ink)' }}>Decision recorded — this deal is read-only</div>
							<div style={{ fontSize: 13, color: 'var(--dlg-muted)' }}>
								Outcome: {deal.decisionOutcome === 'decline' ? 'Declined' : 'Advanced'}. You can view every step and export reports, but the workflow is locked — no further edits or re-runs.
							</div>
						</div>
					</div>
				</div>
			)}
			<Screen deal={deal} />
		</>
	);
}
